import { responsive, useTheme } from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useMemo } from 'react';
import { getContainerStyles } from '@/utils';

export const useStyles = (isDark: boolean) => {
  const { theme } = useTheme();
  return useMemo(
    () => ({
      wrapper: css`
        width: ${theme.spacing.get('100%')};
      `,

      container: css`
        ${getContainerStyles(theme)};
        ${responsive.create({
          paddingTop: {
            default: theme.spacing.get(56),
            md: theme.spacing.get(70),
            lg: theme.spacing.get(100),
          },
          paddingBottom: {
            default: theme.spacing.get(56),
            md: theme.spacing.get(104),
            lg: theme.spacing.get(148),
          },
        })}
      `,

      titleChipContainer: css`
        ${getContainerStyles(theme)};
        border: none;
        ${responsive.create({
          padding: {
            default: `${theme.spacing.add(4, 2)} ${theme.spacing.get(8)}`,
            md: `${theme.spacing.get(8)} ${theme.spacing.get(10)}`,
            lg: `${theme.spacing.get(10)} ${theme.spacing.get(12)}`,
          },
          marginBottom: {
            default: `${theme.spacing.get(184)}`,
            md: `${theme.spacing.get(40)}`,
            lg: `${theme.spacing.get(56)}`,
          },
        })}
      `,

      titleChipLabel: css`
        font-weight: 600;
        font-size: 14px;
      `,

      title: css`
        font-weight: ${theme.fonts.weight.semiBold};
        color: ${theme.colors.commonColors.white};
        color: var(--text-color);
      `,

      description: css`
        color: ${theme.colors.commonColors.white};
        font-size: ${theme.fonts.size.xl};
        max-width: 680px;
        text-align: center;
        color: var(--text-color);
      `,

      headerSection: css`
        margin-bottom: ${theme.spacing.get(72)};
      `,

      fieldContainer: css`
        box-shadow: ${
          isDark
            ? `0 0 4px 0.5px rgba(64, 84, 209, 0.35),
        0 0 8px 2px rgba(64, 84, 209, 0.25)`
            : `0 0 4px 0.5px rgba(23, 206, 202, 0.35),
       0 0 8px 2px rgba(23, 206, 202, 0.25)`
        };
        border-radius: 16px;
        ${responsive.create({
          padding: {
            default: `${theme.spacing.get(16)} `,
            md: `${theme.spacing.get(32)} `,
            lg: `${theme.spacing.get(48)} `,
          },
          maxWidth: {
            default: `100%`,
            md: `${theme.spacing.get(384)}`,

            lg: `${theme.spacing.get(484)}`,
            xl: `${theme.spacing.get(584)}`,
          },
          marginTop: {
            default: `${theme.spacing.get(304)}`,
            md: `${theme.spacing.get(0)}`,
            lg: `${theme.spacing.get(0)}`,
          },
          marginRight: {
            default: `${theme.spacing.get(0)}`,
            md: `${theme.spacing.get(0)}`,
            xl: `${theme.spacing.get(70)}`,
          },
        })}
      `,

      fieldtitle: css`
        text-align: center;
        color: var(--text-color);
      `,

      addressText: css`
        color: var(--text-color);
        text-decoration: none;
        color: inherit; /* or your brand color */
        font-weight: 400;
        line-height: 1.4;
        max-width: 250px;
        ${responsive.create({
          fontSize: {
            default: `${theme.fonts.size.sm}`,
            md: `${theme.fonts.size.sm}`,
            lg: `${theme.fonts.size.md}`,
          },
        })}
      `,

      iconLeft: css`
        ${responsive.create({
          marginRight: {
            default: `${theme.spacing.get(12)}`,
            md: `${theme.spacing.get(16)}`,
            lg: `${theme.spacing.get(24)}`,
          },
        })}
      `,

      chipContainer: css`
        background-color: var(--chip-bg);
        border: none;
        ${responsive.create({
          padding: {
            default: `${theme.spacing.add(4, 2)} ${theme.spacing.get(8)}`,
            md: `${theme.spacing.get(8)} ${theme.spacing.get(10)}`,
            lg: `${theme.spacing.get(10)} ${theme.spacing.get(12)}`,
          },
          marginBottom: {
            default: `${theme.spacing.get(64)}`,
            md: `${theme.spacing.get(32)}`,
            lg: `${theme.spacing.get(32)}`,
          },
        })}
      `,

      infoOverlay: css`
        position: absolute;
        z-index: 2;
      `,

      iconBadge: css`
        background-color: ${theme.colors.commonColors.white};
        aspect-ratio: 1;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        ${responsive.create({
          height: {
            default: '30px',
            md: '36px',
            lg: '48px',
          },
        })}
      `,

      input: css`
        --tf-filled-bg-color: var(--tf-bg-muted-color);
        --tf-bg-color: var(--tf-bg-muted-color);
        --tf-active-color: var(--tf-text-color);
        --tf-border-color: var(--tf-border-color);
        color: var(--tf-text-color);
        &:focus-within {
          --tf-border-color: var(--tf-active-color);
        }
        width: 100%;
      `,

      inputMessage: css`
        min-height: 112px;
      `,

      contactContainer: css`
        ${responsive.create({
          maxWidth: {
            default: '260px',
            md: '320px',
            lg: '320px',
          },
        })}
      `,
    }),
    [theme, isDark],
  );
};
